import React, { useState, useEffect, useMemo } from 'react';
import {
  Building2, FileText, CheckCircle, XCircle, Clock, Eye, EyeOff,
  Save, Plus, Trash2, AlertCircle, History, Landmark,
  Send, MessageSquare, Mail, ChevronDown, ChevronRight,
} from 'lucide-react';
import { journeyApi } from '../../services/journeyApi';
import { api } from '../../services/apiService';

import { RubyGemMark } from '../RubyGemMark';
interface AdminRegistrationsTabProps {
  location: any;
  request: any;
  tenantId: string;
  onRefresh: () => void;
}

interface RequestDocument {
  documentId: string;
  name: string;
  status: 'requested' | 'uploaded' | 'verified' | 'rejected';
  comment?: string;
  fileKey?: string;
  fileName?: string;
  contentType?: string;
  uploadedAt?: string;
  required?: boolean;
  isDefault?: boolean;
  /** Standing guidance from the pack. Survives a rejection; `comment` does not. */
  hint?: string;
}

/** Everything but the last 4, so a reviewer can eyeball a match without the full number on screen. */
const maskNumber = (v: string) => (v.length <= 4 ? v : `${'•'.repeat(v.length - 4)}${v.slice(-4)}`);

const statusStyle = (s: string) => {
  switch (s) {
    case 'verified':  return { cls: 'bg-emerald-100 text-emerald-700', label: 'Approved',     Icon: CheckCircle, color: 'text-emerald-600' };
    case 'uploaded':  return { cls: 'bg-blue-100 text-blue-700',       label: 'Awaiting Review', Icon: Clock,     color: 'text-blue-600' };
    case 'rejected':  return { cls: 'bg-red-100 text-red-700',         label: 'Rejected',      Icon: AlertCircle, color: 'text-red-600' };
    default:          return { cls: 'bg-amber-100 text-amber-700',     label: 'Requested',     Icon: Clock,       color: 'text-amber-600' };
  }
};

const fmtDate = (iso?: string) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtDateTime = (at: string) => {
  if (!at) return '';
  const d = new Date(at);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const timelineIcon = (action: string) => {
  if (action.includes('submitted')) return <Send size={12} className="text-white" />;
  if (action.includes('Info')) return <MessageSquare size={12} className="text-white" />;
  if (action.includes('responded')) return <Mail size={12} className="text-white" />;
  if (action.includes('uploaded') || action.includes('re-uploaded')) return <FileText size={12} className="text-white" />;
  if (action.includes('approved') || action.includes('Approved') || action.includes('Activated')) return <CheckCircle size={12} className="text-white" />;
  if (action.includes('rejected') || action.includes('Rejected') || action.includes('Deactivated') || action.includes('removed')) return <XCircle size={12} className="text-white" />;
  return <Clock size={12} className="text-white" />;
};

/**
 * Every judgement on a document goes onto the LOCATION's journey.
 *
 * Best-effort and deliberately silent: the document action has already
 * committed and the tenant has already been emailed by the time this runs. A
 * failed timeline write must not report the approval as failed — it costs a
 * history entry, not the decision.
 */
async function recordOnJourney(
  tenantId: string,
  locationId: string | undefined,
  locationName: string | undefined,
  summary: string,
) {
  if (!locationId) return;
  try {
    const { journey } = await journeyApi.ensure(tenantId, locationId, {
      subjectType: 'location', locationId,
    });
    await journeyApi.message(journey.journeyId, summary, { stageId: 'compliance' });
  } catch (e: any) {
    console.error('[registrations] could not record on the journey', e?.message);
  }
}

const AdminRegistrationsTab: React.FC<AdminRegistrationsTabProps> = ({ location, request, tenantId, onRefresh }) => {
  const data = location || request;
  const docs: RequestDocument[] = useMemo(() => request?.documents || [], [request?.documents]);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [revealBank, setRevealBank] = useState(false);

  const bankInfo = (data?.bankInfo || {}) as {
    bankName?: string; accountHolder?: string; routingNumber?: string; accountNumber?: string;
  };
  const hasBank = Object.values(bankInfo).some((v) => !!v);

  const timeline = useMemo(() => {
    const events: Array<{ action: string; at: string; note?: string; color: string }> = [];
    if (request?.requestedAt || request?.createdAt) events.push({ action: 'Request submitted', at: request.requestedAt || request.createdAt, color: 'bg-blue-500' });
    if (request?.infoRequested && request?.infoRequestMessage) events.push({ action: 'Info requested', at: request.updatedAt || '', note: request.infoRequestMessage, color: 'bg-amber-500' });
    if (request?.tenantResponse) events.push({ action: 'Tenant responded', at: request.updatedAt || '', note: request.tenantResponse, color: 'bg-rose-500' });
    if (request?.approvedAt) events.push({ action: request.isFree ? 'Approved (Free)' : 'Approved', at: request.approvedAt, color: 'bg-emerald-500' });
    if (request?.activatedAt && request.activatedAt !== request.approvedAt) events.push({ action: 'Activated', at: request.activatedAt, color: 'bg-emerald-600' });
    if (request?.rejectedAt) events.push({ action: 'Rejected', at: request.rejectedAt, note: request.rejectedReason, color: 'bg-red-500' });
    if (request?.deactivationRequestedAt) events.push({ action: 'Deactivation requested', at: request.deactivationRequestedAt, note: request.deactivationReason, color: 'bg-orange-500' });
    if (request?.deactivatedAt) events.push({ action: 'Deactivated', at: request.deactivatedAt, color: 'bg-gray-500' });
    if (Array.isArray(request?.timeline)) {
      request.timeline.forEach((ev: any) => {
        const color = ev.type?.includes('approved') || ev.type?.includes('verified') ? 'bg-emerald-500'
          : ev.type?.includes('rejected') || ev.type?.includes('removed') ? 'bg-red-500'
          : ev.type?.includes('uploaded') || ev.type?.includes('reuploaded') || ev.type?.includes('requested') ? 'bg-blue-500'
          : 'bg-gray-500';
        events.push({ action: ev.action, at: ev.at, note: ev.note, color });
      });
    }
    events.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    return events;
  }, [request]);

  // ---- Business identity editing ----
  const canEditBusinessIdentity =
    request?.status === 'pending' || request?.status === 'info_requested';
  const [ein, setEin] = useState<string>(data?.ein || '');
  const [legalEntityName, setLegalEntityName] = useState<string>(data?.legalEntityName || '');
  const [identitySaving, setIdentitySaving] = useState(false);
  const [identityError, setIdentityError] = useState<string | null>(null);

  useEffect(() => {
    setEin(data?.ein || '');
    setLegalEntityName(data?.legalEntityName || '');
    setIdentityError(null);
  }, [data?.ein, data?.legalEntityName]);

  const identityDirty =
    (ein || '') !== (data?.ein || '') ||
    (legalEntityName || '') !== (data?.legalEntityName || '');

  const handleSaveIdentity = async () => {
    if (ein && !/^\d{2}-\d{7}$/.test(ein)) {
      setIdentityError('EIN must be formatted XX-XXXXXXX');
      return;
    }
    try {
      setIdentitySaving(true);
      setIdentityError(null);
      await api.locationBilling.updateRequest(request.requestId, { tenantId, ein, legalEntityName });
      onRefresh();
    } catch (err: any) {
      setIdentityError(err.message || 'Failed to save');
    } finally {
      setIdentitySaving(false);
    }
  };

  // ---- Doc actions ----
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectDocId, setRejectDocId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [newDocComment, setNewDocComment] = useState('');

  const handleView = async (documentId: string) => {
    try {
      const res: any = await api.locationBilling.getDocumentViewUrl(request.requestId, tenantId, documentId);
      window.open(res.viewUrl, '_blank');
    } catch (err: any) {
      setActionMessage('Failed to view document: ' + (err.message || 'Unknown error'));
    }
  };

  const handleApprove = async (doc: RequestDocument) => {
    try {
      setActionLoading(true);
      await api.locationBilling.documentAction(request.requestId, {
        tenantId, documentId: doc.documentId, action: 'approve',
      });
      setActionMessage(`"${doc.name}" approved — tenant notified`);
      recordOnJourney(tenantId, request?.locationId, request?.name, `"${doc.name}" verified.`);
      onRefresh();
    } catch (err: any) {
      setActionMessage('Failed to approve: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (doc: RequestDocument) => {
    if (!rejectComment.trim()) return;
    try {
      setActionLoading(true);
      await api.locationBilling.documentAction(request.requestId, {
        tenantId, documentId: doc.documentId, action: 'reject', comment: rejectComment.trim(),
      });
      setActionMessage(`"${doc.name}" rejected — tenant notified`);
      recordOnJourney(tenantId, request?.locationId, request?.name,
        `"${doc.name}" rejected: ${rejectComment.trim()}`);
      setRejectDocId(null);
      setRejectComment('');
      onRefresh();
    } catch (err: any) {
      setActionMessage('Failed to reject: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddDoc = async () => {
    if (!newDocName.trim()) return;
    try {
      setActionLoading(true);
      await api.locationBilling.addDocument(request.requestId, {
        tenantId,
        name: newDocName.trim(),
        comment: newDocComment.trim() || undefined,
      });
      setActionMessage(`Requested: "${newDocName.trim()}" — tenant notified`);
      setShowAddDoc(false);
      setNewDocName('');
      setNewDocComment('');
      onRefresh();
    } catch (err: any) {
      setActionMessage('Failed to add document: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveDoc = async (doc: RequestDocument) => {
    if (doc.isDefault) return;
    if (doc.status === 'verified') return;
    try {
      setActionLoading(true);
      await api.locationBilling.removeDocument(request.requestId, doc.documentId, tenantId);
      setActionMessage(`Removed: "${doc.name}"`);
      onRefresh();
    } catch (err: any) {
      setActionMessage('Failed to remove: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {actionMessage && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-blue-700">{actionMessage}</p>
          <button onClick={() => setActionMessage(null)} className="text-blue-400 hover:text-blue-600 text-sm">Dismiss</button>
        </div>
      )}

      {/* Business Registration */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
            <Building2 size={16} className="text-stone-500" />
            Business Registration
          </h3>
          {canEditBusinessIdentity && identityDirty && (
            <button
              onClick={handleSaveIdentity}
              disabled={identitySaving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium"
            >
              {identitySaving ? <RubyGemMark size={12} drawing color="currentColor" /> : <Save size={12} />}
              Save
            </button>
          )}
        </div>
        {canEditBusinessIdentity ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-medium text-stone-400 uppercase tracking-wide mb-1 block">EIN</label>
                <input
                  type="text"
                  value={ein}
                  onChange={(e) => setEin(e.target.value)}
                  placeholder="XX-XXXXXXX"
                  className="w-full rounded-lg bg-white border border-stone-300 text-stone-900 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-stone-400 uppercase tracking-wide mb-1 block">Legal Entity Name</label>
                <input
                  type="text"
                  value={legalEntityName}
                  onChange={(e) => setLegalEntityName(e.target.value)}
                  className="w-full rounded-lg bg-white border border-stone-300 text-stone-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                />
              </div>
            </div>
            {identityError && <p className="text-xs text-red-600 mt-2">{identityError}</p>}
            <p className="text-[11px] text-stone-400 mt-3">Editable while request is pending. Locks after approval.</p>
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="py-2.5 border-b border-stone-100">
              <p className="text-[11px] font-medium text-stone-400 uppercase tracking-wide mb-1">EIN</p>
              <p className="text-sm font-medium text-stone-800 font-mono">{data?.ein || 'Not provided'}</p>
            </div>
            <div className="py-2.5 border-b border-stone-100">
              <p className="text-[11px] font-medium text-stone-400 uppercase tracking-wide mb-1">Legal Entity Name</p>
              <p className="text-sm font-medium text-stone-800">{data?.legalEntityName || 'Not provided'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Bank details. READ-ONLY here on purpose: the tenant enters these on
          their own Registrations tab and they stay editable for the life of the
          store. The admin's job is to check them against the void check in the
          pack below, not to type them in on the tenant's behalf. */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
            <Landmark size={16} className="text-stone-500" />
            Bank Information
          </h3>
          {hasBank && (
            <button
              onClick={() => setRevealBank((v) => !v)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-stone-500 hover:text-stone-800 hover:bg-stone-100"
            >
              {revealBank ? <EyeOff size={12} /> : <Eye size={12} />}
              {revealBank ? 'Hide' : 'Reveal'}
            </button>
          )}
        </div>
        {!hasBank ? (
          <p className="text-sm text-stone-400">
            The tenant has not entered bank details yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="py-2.5 border-b border-stone-100">
              <p className="text-[11px] font-medium text-stone-400 uppercase tracking-wide mb-1">Bank Name</p>
              <p className="text-sm font-medium text-stone-800">{bankInfo.bankName || 'Not provided'}</p>
            </div>
            <div className="py-2.5 border-b border-stone-100">
              <p className="text-[11px] font-medium text-stone-400 uppercase tracking-wide mb-1">Account Holder</p>
              <p className="text-sm font-medium text-stone-800">{bankInfo.accountHolder || 'Not provided'}</p>
            </div>
            <div className="py-2.5 border-b border-stone-100">
              <p className="text-[11px] font-medium text-stone-400 uppercase tracking-wide mb-1">Routing Number</p>
              <p className="text-sm font-medium text-stone-800 font-mono">
                {bankInfo.routingNumber ? (revealBank ? bankInfo.routingNumber : maskNumber(bankInfo.routingNumber)) : 'Not provided'}
              </p>
            </div>
            <div className="py-2.5 border-b border-stone-100">
              <p className="text-[11px] font-medium text-stone-400 uppercase tracking-wide mb-1">Account Number</p>
              <p className="text-sm font-medium text-stone-800 font-mono">
                {bankInfo.accountNumber ? (revealBank ? bankInfo.accountNumber : maskNumber(bankInfo.accountNumber)) : 'Not provided'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
            <FileText size={16} className="text-stone-500" />
            Documents
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 font-medium">
              {docs.filter(d => d.status === 'verified').length}/{docs.length}
            </span>
          </h3>
          {canEditBusinessIdentity && (
            <button
              onClick={() => setShowAddDoc(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium"
            >
              <Plus size={12} /> Request Document
            </button>
          )}
        </div>

        {showAddDoc && (
          <div className="mb-4 p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-3">
            <div>
              <label className="text-xs text-stone-500 font-medium block mb-1">Document Name</label>
              <input
                type="text"
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                placeholder="e.g., Partner ID, Bank Confirmation"
                className="w-full bg-white border border-stone-300 rounded-lg px-3 py-2 text-stone-900 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-stone-500 font-medium block mb-1">Note to Tenant (optional)</label>
              <textarea
                value={newDocComment}
                onChange={(e) => setNewDocComment(e.target.value)}
                className="w-full bg-white border border-stone-300 rounded-lg px-3 py-2 text-stone-900 text-sm h-16 resize-none"
                placeholder="Instructions for the tenant..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowAddDoc(false); setNewDocName(''); setNewDocComment(''); }}
                className="px-3 py-1.5 bg-stone-200 hover:bg-stone-300 text-stone-800 rounded-lg text-xs font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDoc}
                disabled={actionLoading || !newDocName.trim()}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium inline-flex items-center gap-1.5"
              >
                {actionLoading ? <RubyGemMark size={12} drawing color="currentColor" /> : <Plus size={12} />}
                Request
              </button>
            </div>
          </div>
        )}

        {docs.length === 0 ? (
          <div className="text-center py-8 text-stone-400 text-sm">No documents requested yet.</div>
        ) : (
          <div className="space-y-2">
            {docs.map((doc) => {
              const s = statusStyle(doc.status);
              const canView = !!doc.fileKey && (doc.status === 'uploaded' || doc.status === 'verified' || doc.status === 'rejected');
              const canApproveReject = doc.status === 'uploaded';
              const canRemove = !doc.isDefault && doc.status !== 'verified' && canEditBusinessIdentity;
              return (
                <div key={doc.documentId} className="bg-stone-50 rounded-xl border border-stone-100 overflow-hidden">
                  <div className="flex items-center gap-3 p-3">
                    <div className="w-8 h-8 rounded-lg bg-white border border-stone-200 flex items-center justify-center shrink-0">
                      <s.Icon size={14} className={s.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-stone-800 truncate">{doc.name}</p>
                        {doc.isDefault && <span className="text-[9px] px-1.5 py-0.5 rounded bg-stone-200 text-stone-600 font-medium">DEFAULT</span>}
                      </div>
                      {doc.hint && <p className="text-[11px] text-stone-500 mt-0.5">{doc.hint}</p>}
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>
                        {doc.fileName && (
                          <span className="text-[11px] text-stone-500 truncate max-w-[220px]">
                            {doc.fileName}{doc.uploadedAt ? ` · ${fmtDate(doc.uploadedAt)}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canView && (
                        <button
                          onClick={() => handleView(doc.documentId)}
                          className="p-2 rounded-lg hover:bg-stone-200 text-stone-500 hover:text-stone-700 min-h-[36px] min-w-[36px] flex items-center justify-center"
                          title="View"
                        >
                          <Eye size={14} />
                        </button>
                      )}
                      {canApproveReject && (
                        <>
                          <button
                            onClick={() => handleApprove(doc)}
                            disabled={actionLoading}
                            className="text-xs text-emerald-600 hover:text-white px-2 py-1.5 rounded-lg hover:bg-emerald-600 transition-colors flex items-center gap-1 min-h-[32px] disabled:opacity-50"
                            title="Approve"
                          >
                            <CheckCircle size={11} /> Approve
                          </button>
                          <button
                            onClick={() => setRejectDocId(doc.documentId)}
                            className="text-xs text-red-600 hover:text-white px-2 py-1.5 rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1 min-h-[32px]"
                            title="Reject"
                          >
                            <XCircle size={11} /> Reject
                          </button>
                        </>
                      )}
                      {canRemove && (
                        <button
                          onClick={() => handleRemoveDoc(doc)}
                          className="p-2 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-600 min-h-[36px] min-w-[36px] flex items-center justify-center"
                          title="Remove requirement"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  {rejectDocId === doc.documentId && (
                    <div className="px-3 pb-3 pt-2 border-t border-stone-200 bg-white">
                      <label className="text-xs text-stone-500 mb-1 block">Rejection reason (required)</label>
                      <textarea
                        value={rejectComment}
                        onChange={(e) => setRejectComment(e.target.value)}
                        className="w-full bg-white border border-stone-300 rounded-lg px-3 py-2 text-stone-900 text-sm h-20 resize-none"
                        placeholder="Explain what's wrong so the tenant can fix it..."
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <button
                          onClick={() => { setRejectDocId(null); setRejectComment(''); }}
                          className="px-3 py-1.5 bg-stone-200 hover:bg-stone-300 text-stone-800 rounded-lg text-xs font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          disabled={!rejectComment.trim() || actionLoading}
                          onClick={() => handleReject(doc)}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium"
                        >
                          Reject &amp; Notify
                        </button>
                      </div>
                    </div>
                  )}
                  {doc.status === 'rejected' && doc.comment && (
                    <div className="px-3 pb-3 pt-2 bg-red-50 border-t border-red-200">
                      <p className="text-[11px] font-medium text-red-700 uppercase tracking-wide mb-0.5">Rejection reason</p>
                      <p className="text-xs text-red-700">{doc.comment}</p>
                    </div>
                  )}
                  {doc.status === 'requested' && doc.comment && (
                    <div className="px-3 pb-3 pt-2 bg-amber-50 border-t border-amber-200">
                      <p className="text-xs text-amber-800">Note: {doc.comment}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Activity Timeline */}
      {timeline.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 overflow-hidden">
          <button
            onClick={() => setTimelineOpen(!timelineOpen)}
            className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-stone-50 transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
              <History size={14} className="text-stone-600" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-stone-800">Activity</span>
              <span className="text-xs text-stone-400 ml-2">{timeline[timeline.length - 1].action} · {fmtDateTime(timeline[timeline.length - 1].at)}</span>
            </div>
            <span className="text-[10px] text-stone-400 shrink-0">{timeline.length} event{timeline.length !== 1 ? 's' : ''}</span>
            {timelineOpen ? <ChevronDown size={16} className="text-stone-400" /> : <ChevronRight size={16} className="text-stone-400" />}
          </button>
          {timelineOpen && (
            <div className="border-t border-stone-100 px-5 py-4">
              {timeline.map((ev, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full ${ev.color} flex items-center justify-center shrink-0`}>
                      {timelineIcon(ev.action)}
                    </div>
                    {i < timeline.length - 1 && <div className="w-px flex-1 bg-stone-200 my-0.5" />}
                  </div>
                  <div className="flex-1 min-w-0 pb-4 pt-1">
                    <p className="text-sm font-medium text-stone-700 leading-5">{ev.action}</p>
                    <p className="text-[11px] text-stone-400 mt-0.5">{fmtDateTime(ev.at)}</p>
                    {ev.note && <p className="text-xs text-stone-500 mt-1.5 bg-stone-50 px-3 py-2 rounded-lg border border-stone-100">{ev.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminRegistrationsTab;
